import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaLongArrowAltLeft, FaPlay } from "react-icons/fa";
import { MdMenuBook } from "react-icons/md";
import { Dialog, DialogPanel } from "@headlessui/react";
import { IoMdClose } from "react-icons/io";
import { quizGetDeckDetail, quizGetDeckCards } from "./quizApiService";
import Button from "../../components/ui/Button";
import DeckCardsPanel from "../../components/DeckCardsPanel";
import Loading from "../../components/Loading";

const LEARNING_APP_BASE = "https://app.zubrus.lt/assignments-deck-details";

const isMobileDevice = () => {
  if (typeof window === "undefined") return false;
  const isSmallScreen = window.matchMedia("(max-width: 768px)").matches;
  const isMobileUA =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );
  return isSmallScreen || isMobileUA;
};

export default function QuizDeckDetail() {
  const { t } = useTranslation();
  const { deckId } = useParams();
  const navigate = useNavigate();

  const [deckData, setDeckData] = useState(null);
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isLearningMode, setIsLearningMode] = useState(false);

  const fetchData = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const [deckRes, cardsRes] = await Promise.all([
        quizGetDeckDetail(deckId),
        quizGetDeckCards(deckId),
      ]);
      setDeckData(deckRes?.data || deckRes);
      setCards(cardsRes?.data || cardsRes || []);
    } catch (err) {
      console.error("Failed to load deck detail:", err);
      if (!silent) {
        setError(err?.response?.data?.error || err?.message || "Failed to load deck");
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [deckId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const learningUrl = `${LEARNING_APP_BASE}/${deckId}`;

  const handleOpenLearning = () => {
    // On mobile, open full-screen in a new tab (iframe is cramped / limited)
    if (isMobileDevice()) {
      window.open(learningUrl, "_blank", "noopener,noreferrer");
      return;
    }
    setIsLearningMode(true);
  };

  if (loading) {
    return (
      <div className="flex flex-1 w-full items-center justify-center px-4 py-24">
        <div className="flex flex-col items-center gap-3">
          <Loading />
          <p className="text-sm font-medium text-gray-500">{t("loading")}</p>
        </div>
      </div>
    );
  }

  if (error || !deckData) {
    return (
      <div className="flex-1 flex items-center justify-center px-4 py-20">
        <div className="text-center">
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            {error || t("quiz.deckNotFound")}
          </h2>
          <Button
            variant="primary"
            size="lg"
            className="mt-4"
            onClick={() => navigate("/quiz")}
          >
            {t("quiz.backToHome")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 w-full">
      <div className="p-5 max-w-[1500px] mx-auto">
        {/* Header */}
        <div className="flex flex-wrap justify-between items-center gap-4 mb-5">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/quiz")}
              className="border cursor-pointer rounded-full flex justify-center items-center size-10"
              aria-label={t("quiz.backToHome")}
            >
              <FaLongArrowAltLeft />
            </button>
          </div>
          <div className="flex items-center gap-4 text-base font-semibold flex-wrap">
            <Button variant="primary" size="lg" onClick={handleOpenLearning}>
              <MdMenuBook /> {t("Zubrinti")}
            </Button>
            <Button
              variant="primary"
              size="lg"
              onClick={() => navigate(`/quiz/deck/${deckId}/select-cards`)}
            >
              <FaPlay /> {t("quiz.playQuiz")}
            </Button>
          </div>
        </div>

        {/* Deck Info */}
        <div className="bg-white rounded-2xl border border-gray-100 p-5 flex flex-wrap items-center gap-5 mb-6">
          {deckData.largeImageUrl && (
            <img
              src={deckData.largeImageUrl}
              alt={deckData.deckName}
              className="w-24 h-24 rounded-xl object-cover border"
            />
          )}
          <div className="flex flex-col gap-1.5">
            <h2 className="text-lg font-bold text-gray-900">{deckData.deckName}</h2>
            {deckData.mainSubject && (
              <p className="text-sm text-gray-600">
                <span className="font-medium text-gray-800">{t("subject")}:</span> {deckData.mainSubject}
              </p>
            )}
            {deckData.schoolGrades?.length > 0 && (
              <p className="text-sm text-gray-600">
                <span className="font-medium text-gray-800">{t("gradeLabel")}:</span> {deckData.schoolGrades.join(", ")}
              </p>
            )}
            <p className="text-sm text-gray-600">
              <span className="font-medium text-gray-800">{t("cards")}:</span> {cards.length}
            </p>
            {deckData.country && (
              <p className="text-sm text-gray-600">
                <span className="font-medium text-gray-800">{t("country")}:</span> {deckData.country}
              </p>
            )}
          </div>
        </div>

        <div className="my-5">
          <DeckCardsPanel
            cards={cards}
            links={deckData?.cards}
            deckId={deckId}
            enableReorder
            onOrderSaved={() => fetchData({ silent: true })}
            storageKey="quiz_deck_cards_view"
            showHeader={false}
            showAnswer={false}
            showHints={false}
            showAiBadges={false}
            emptyLabel={t("nocard")}
          />
        </div>

        {/* Learning Mode - Zubrus app iframe */}
        {isLearningMode && (
          <Dialog open={isLearningMode} as="div" className="relative z-50" onClose={() => setIsLearningMode(false)}>
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
              <DialogPanel className="w-full max-w-6xl bg-white rounded-3xl relative h-[90vh] flex flex-col">
                <IoMdClose
                  className="absolute top-4 right-4 text-gray-600 cursor-pointer z-10"
                  onClick={() => setIsLearningMode(false)}
                  size={22}
                />

                <div className="p-4 pt-12 flex flex-col flex-1 min-h-0 gap-3">
                  <div className="flex-1 min-h-0 rounded-lg overflow-hidden border border-gray-200">
                    <iframe
                      src={learningUrl}
                      title={t("Zubrinti")}
                      className="w-full h-full border-0"
                      allow="microphone; camera"
                    />
                  </div>
                </div>
              </DialogPanel>
            </div>
          </Dialog>
        )}
      </div>
    </div>
  );
}
